import { Card, Form, Typography, message } from 'antd';
import { useEffect, useState } from 'react';
import { Trans } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import classNames from 'classnames';

import {
	CheckBoxField,
	CheckBoxGroup,
	FormItem,
	RadioGroup,
	SubmitButton,
	TextField,
} from 'componets/form';
import { Loading } from 'componets/ui';

import { FieldData } from 'models';
import { Object, QuestionnaireOption, QuestionnaireQuest } from 'models';

import { useAppDispatch, useAppSelector, useTranslate } from 'hooks';

import { getAML, sendQuiz } from 'features/questionnaire/actions';
import { getUserProfile } from 'features/user/actions';

import { language } from 'utils';
import Utils from 'utils/utils';
import { RequestStatus, Rules, OptionType, Paths } from 'utils/constants';

import { selectUser } from 'selectors';

import s from './index.module.scss';

const lang = language;

export const DOCUMENTS: any = [
	{
		en: 'https://crowdpear.com/wp-content/uploads/2025/09/Description_of_risks_related_to_investment_in_crowdpear_platform.pdf',
		lt: 'https://crowdpear.com/wp-content/uploads/2025/09/Riziku_susijusiu_su_investavimu_Сrowdpear_platformoje_aprasas.pdf',
		ro: 'https://crowdpear.com/wp-content/uploads/2025/09/Description_of_risks_related_to_investment_in_crowdpear_platform.pdf',
		pt: 'https://crowdpear.com/wp-content/uploads/2025/09/Description_of_risks_related_to_investment_in_crowdpear_platform.pdf',
	},
	{
		en: 'https://crowdpear.com/wp-content/uploads/2026/01/Usage_Agreement_Of_The_Crowdfunding_Platform_Crowdpear.pdf ',
		lt: 'https://crowdpear.com/wp-content/uploads/2026/01/Sutelktinio_finansavimo_platformos_Сrowdpear_naudojimosi_sutartis.pdf',
		ro: 'https://crowdpear.com/wp-content/uploads/2026/01/Usage_Agreement_Of_The_Crowdfunding_Platform_Crowdpear.pdf',
		pt: 'https://crowdpear.com/wp-content/uploads/2026/01/Usage_Agreement_Of_The_Crowdfunding_Platform_Crowdpear.pdf',
	},
	{
		en: 'https://crowdpear.com/wp-content/uploads/2025/11/Money_laundering_and_terrorist_financing_prevention_policy.pdf',
		lt: 'https://crowdpear.com/wp-content/uploads/2025/11/Pinigu_plovimo_ir_teroristu_finansavimo_prevencijos_politika.pdf',
		ro: 'https://crowdpear.com/wp-content/uploads/2025/11/Money_laundering_and_terrorist_financing_prevention_policy.pdf',
		pt: 'https://crowdpear.com/wp-content/uploads/2025/11/Money_laundering_and_terrorist_financing_prevention_policy.pdf',
	},
	{
		en: 'https://crowdpear.com/wp-content/uploads/2023/09/Procedure_for_assessing_the_suitability_of_crowdfunding_transactions_and_modelling_of_the_capacity_for_loss.pdf',
		lt: 'https://crowdpear.com/wp-content/uploads/2023/09/Sutelktinio_finansavimo_sandoriu_tinkamumo_vertinimo_bei_gebejimo_prisiimti_nuostolius_modeliavimo_tvarka.pdf',
		ro: 'https://crowdpear.com/wp-content/uploads/2023/09/Procedure_for_assessing_the_suitability_of_crowdfunding_transactions_and_modelling_of_the_capacity_for_loss.pdf',
	},
	{
		en: 'https://crowdpear.com/wp-content/uploads/2025/09/Rates_applicable_to_the_users_of_the_crowdfunding_platform_Crowdpear.pdf',
		lt: 'https://crowdpear.com/wp-content/uploads/2025/09/Sutelktinio_finansavimo_platformos_Сrowdpear_naudotojams_taikomi_ikainiai.pdf',
		ro: 'https://crowdpear.com/wp-content/uploads/2025/09/Rates_applicable_to_the_users_of_the_crowdfunding_platform_Crowdpear.pdf',
		pt: 'https://crowdpear.com/wp-content/uploads/2025/09/Rates_applicable_to_the_users_of_the_crowdfunding_platform_Crowdpear.pdf',
	},
];

const MAX_LENGHT = 500;

export const AML = () => {
	const [form] = Form.useForm();

	const [fields, setFields] = useState<FieldData[]>([]);

	const navigate = useNavigate();

	const { aml, status: requestStatus } = useAppSelector((state) => state.questionnaire);

	const user = useAppSelector(selectUser);

	const {
		amlFirstCheckboxText,
		amlSecondCheckboxText,
		amlThirdCheckboxText,
		amlFourthCheckboxText,
		sendQuestionnaireText,
		amlFailedText,
		amlPassedText,
		thanksForAnswersText,
	} = useTranslate([
		'amlFirstCheckboxText',
		'amlSecondCheckboxText',
		'amlThirdCheckboxText',
		'amlFourthCheckboxText',
		'sendQuestionnaireText',
		'amlFailedText',
		'amlPassedText',
		'thanksForAnswersText',
	]);

	const dispatch = useAppDispatch();

	useEffect(() => {
		dispatch(getAML());
	}, [dispatch]);

	const onFinish = (data: Object) => {
		const body = Utils.formatQuizAnswers(aml, data);

		dispatch(sendQuiz({ quizId: aml.id, body, isAML: true }))
			.unwrap()
			.then(() => {
				dispatch(getUserProfile())
					.unwrap()
					.then(() => {
						message.success(thanksForAnswersText);
						navigate(Paths.QUESTIONNAIRE_SUCCESS);
					})
					.catch(() => {});
			})
			.catch((e) => {
				message.warn(e.message);
			});
	};

	const isLoading = requestStatus === RequestStatus.PENDING;

	if (!aml.texts) return <Loading />;

	return (
		<div className={s.root}>
			<Typography.Title level={5}>{aml.texts[lang]}</Typography.Title>

			<Typography.Text>
				<Trans i18nKey="amlQuizText" components={{ s: <br /> }} />
			</Typography.Text>

			<div>
				<Form
					onFinish={onFinish}
					onFieldsChange={setFields}
					fields={fields}
					form={form}
					className={s.form}
					scrollToFirstError
				>
					<div className={s.formWrapper}>
						{aml.quests.filter((item: any) => !!item.texts[lang])
							.map((item: QuestionnaireQuest) => {
								const Component = item.quest_type === 'SINGLE' ? RadioGroup : CheckBoxGroup;

								const options = item.options
									.filter((item: any) => !!item.texts[lang])
									.map((item: QuestionnaireOption) => ({
										value: item.id,
										label: item.texts[lang],
								}));

								const isText = item.options[0]?.option_type === OptionType.TEXT;
								const rules = isText
									? [Rules.MAX_LENGTH(item.id, MAX_LENGHT)]
									: [Rules.REQUIRED_ANSWER(item.id)];

								if (!item.options[0]?.option_type) return '';
								return (
									<Card key={item.id} className={s.card}>
										<div>
											<div
												className={s.question}
												dangerouslySetInnerHTML={{
													__html: item.texts[lang]?.replace(/href/g, "target='_blank' href"),
												}}
											/>

											<FormItem
												name={item.id}
												rules={rules}
												className={classNames({ checkboxQuiz: !isText })}
											>
												{isText ? <TextField label="" /> : <Component options={options} isQuiz />}
											</FormItem>
										</div>
									</Card>
								);
							})}
					</div>

					<Typography.Text className={s.text}>
						{user.quizStatuses.KYC.status === 'FAILED' ? amlFailedText : amlPassedText}
					</Typography.Text>

					<div className={s.checkboxes}>
						<FormItem
							name="first"
							className={classNames(s.checkbox, 'checkboxQuiz')}
							rules={[Rules.REQUIRED_CHECKBOX]}
						>
							<CheckBoxField text={amlFirstCheckboxText} />
						</FormItem>

						<FormItem
							name="second"
							className={classNames(s.checkbox, 'checkboxQuiz')}
							rules={[Rules.REQUIRED_CHECKBOX]}
						>
							<CheckBoxField text={amlSecondCheckboxText} />
						</FormItem>

						<FormItem
							name="third"
							className={classNames(s.checkbox, 'checkboxQuiz')}
							rules={[Rules.REQUIRED_CHECKBOX]}
						>
							<CheckBoxField text={amlThirdCheckboxText} />
						</FormItem>

						<FormItem
							name="fourth"
							className={classNames(s.checkbox, 'checkboxQuiz')}
							rules={[Rules.REQUIRED_CHECKBOX]}
						>
							<CheckBoxField text={amlFourthCheckboxText} />
						</FormItem>

						<FormItem
							name="fifth"
							className={classNames(s.checkbox, 'checkboxQuiz')}
							rules={[Rules.REQUIRED_CHECKBOX]}
						>
							<CheckBoxField
								text={
									<Trans
										i18nKey="amlFifthCheckboxText"
										components={{
											link_one: <a target="_blank" href={DOCUMENTS[0][lang]} rel="noreferrer" />,
											link_two: <a target="_blank" href={DOCUMENTS[1][lang]} rel="noreferrer" />,
											link_three: <a target="_blank" href={DOCUMENTS[2][lang]} rel="noreferrer" />,
											link_four: <a target="_blank" href={DOCUMENTS[3][lang]} rel="noreferrer" />,
											link_five: <a target="_blank" href={DOCUMENTS[4][lang]} rel="noreferrer" />,
										}}
									/>
								}
							/>
						</FormItem>
					</div>

					<SubmitButton
						classNameWrapper={s.submitWrapper}
						text={sendQuestionnaireText}
						loading={isLoading}
						className={s.send}
					/>
				</Form>
			</div>
		</div>
	);
};
